import { useState, useEffect, useMemo, useCallback } from "react";
import { supabaseAdmin as supabase } from "@/lib/supabaseAdmin";
import { useDebounce } from "@/hooks/useDebounce";
import ConfirmDialog from "./ConfirmDialog";

interface OrderRow {
  id: string;
  user_id: string;
  product_id: string;
  product_name: string;
  amount: number;
  type: string;
  status: string;
  completion_percentage: number | null;
  created_at: string;
  return_rate: number | null;
  profit: number | null;
  extra_profit: number | null;
  selling_amount: number | null;
  quantity: number | null;
  category: string | null;
  image_url: string | null;
  admin_created: boolean | null;
  products?: { type: string } | null;
}

interface ProfileMap {
  [id: string]: { name: string; phone: string };
}

function computeRefundAmount(order: OrderRow): number {
  const qty = order.quantity ?? 1;
  return (order.amount + (order.profit ?? 0) + (order.extra_profit ?? 0)) * qty;
}

export default function AdminOrders() {
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [profiles, setProfiles] = useState<ProfileMap>();
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [activeFilter, setActiveFilter] = useState<"all" | "trial" | "vip">("all");
  const [searchTerm, setSearchTerm] = useState("");

  const [completingId, setCompletingId] = useState<string | null>(null);
  const [confirmOrder, setConfirmOrder] = useState<OrderRow | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [editingProgressId, setEditingProgressId] = useState<string | null>(null);
  const [editingProgressValue, setEditingProgressValue] = useState<string>("");
  const [updatingProgressId, setUpdatingProgressId] = useState<string | null>(null);

  const showToast = useCallback((message: string, type: "success" | "error") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // 穩定 fetchData：useCallback + 空依賴，只執行一次
  const fetchData = useCallback(async () => {
    setLoading(true);
    setFetchError("");
    try {
      const [ordersRes, profilesRes] = await Promise.all([
        supabase
          .from("orders")
          .select(
            "id, user_id, product_id, product_name, amount, type, status, completion_percentage, created_at, return_rate, profit, extra_profit, selling_amount, quantity"
          )
          .order("created_at", { ascending: false })
          .limit(200),
        supabase.from("profiles").select("id, name, phone").limit(500),
      ]);

      if (ordersRes.error) {
        console.error("orders fetch error:", ordersRes.error);
        setFetchError(`訂單讀取失敗：${ordersRes.error.message}`);
        setOrders([]);
      } else {
        const orderList = (ordersRes.data || []) as OrderRow[];

        // 對 type 為空的訂單，補查 products.type 來做 fallback 分類
        const missingTypeIds = orderList
          .filter((o) => !o.type && o.product_id)
          .map((o) => o.product_id);

        let productTypeMap: Record<string, string> = {};
        if (missingTypeIds.length > 0) {
          const uniqueIds = [...new Set(missingTypeIds)];
          const { data: productsData, error: productsError } = await supabase
            .from("products")
            .select("id, type")
            .in("id", uniqueIds);
          if (!productsError && productsData) {
            (productsData as { id: string; type: string }[]).forEach((p) => {
              productTypeMap[p.id] = p.type;
            });
          }
        }

        // 把補查到的 type 注入每筆 order 的 products 欄位
        const enrichedOrders = orderList.map((o) => {
          const fallbackType = productTypeMap[o.product_id] || "";
          return {
            ...o,
            products: o.type ? null : { type: fallbackType },
          };
        });

        setOrders(enrichedOrders);
      }

      if (profilesRes.error) {
        console.error("profiles fetch error:", profilesRes.error);
      } else {
        const map: ProfileMap = {};
        (profilesRes.data || []).forEach((p: { id: string; name: string | null; phone: string | null }) => {
          map[p.id] = { name: p.name || "未知用戶", phone: p.phone || "—" };
        });
        setProfiles(map);
      }
    } catch (err: unknown) {
      console.error("fetchData exception:", err);
      setFetchError("資料載入過程發生異常，請重新整理頁面");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCompleteClick = (order: OrderRow) => {
    if (order.status === "completed") return;
    setConfirmOrder(order);
    setShowConfirm(true);
  };

  const handleConfirmComplete = async () => {
    if (!confirmOrder) return;
    setShowConfirm(false);
    setCompletingId(confirmOrder.id);

    try {
      const { data, error } = await supabase.rpc("complete_order_and_refund", {
        p_order_id: confirmOrder.id,
      });

      if (error) {
        console.error("[complete_order_and_refund] RPC error:", error.code, error.message, error.details);
        showToast(`返款失敗：${error.message}`, "error");
        setCompletingId(null);
        return;
      }

      if (data && data.success) {
        const refund = data.refund_amount as number;

        // ── 扣減該筆訂單對應的待結算利潤（歸零）──
        const profitDeduct =
          ((confirmOrder.profit ?? 0) + (confirmOrder.extra_profit ?? 0)) *
          (confirmOrder.quantity ?? 1);
        if (profitDeduct > 0) {
          try {
            const { data: walletData, error: walletErr } = await supabase
              .from("wallets")
              .select("pending_profits")
              .eq("user_id", confirmOrder.user_id)
              .maybeSingle();
            if (!walletErr && walletData) {
              const newPending = Math.max(
                0,
                (walletData.pending_profits ?? 0) - profitDeduct,
              );
              await supabase
                .from("wallets")
                .update({ pending_profits: newPending })
                .eq("user_id", confirmOrder.user_id);
              await supabase.from("wallet_transactions").insert({
                user_id: confirmOrder.user_id,
                order_id: confirmOrder.id,
                amount: -profitDeduct,
                type: "profit_settled",
                description: `訂單「${confirmOrder.product_name}」完成結算，待結算利潤歸零`,
              });
              // 通知前台錢包自動刷新
              window.dispatchEvent(new CustomEvent("wallet:refresh"));
            }
          } catch (pendingErr) {
            console.error(
              "[AdminOrders] 待結算利潤扣減失敗:",
              pendingErr,
            );
            showToast(
              `訂單已返款，但待結算利潤扣減失敗，請至會員管理手動調整`,
              "error",
            );
          }
        }

        showToast(
          `狀態已變更，已成功返款 NT$${refund.toLocaleString()} 至該會員錢包`,
          "success",
        );
        await fetchData();
      } else {
        const errMsg = (data && data.error) || "操作失敗";
        console.error("[complete_order_and_refund] logic error:", errMsg);
        showToast(errMsg, "error");
      }
    } catch (err) {
      console.error("[complete_order_and_refund] exception:", err);
      showToast("系統異常，請稍後重試", "error");
    } finally {
      setCompletingId(null);
      setConfirmOrder(null);
    }
  };

  const handleProgressBlur = async (orderId: string) => {
    const raw = editingProgressValue.trim();
    if (!raw) {
      setEditingProgressId(null);
      return;
    }
    let val = parseInt(raw, 10);
    if (Number.isNaN(val)) {
      setEditingProgressId(null);
      return;
    }
    // Clamp 0-100
    val = Math.max(0, Math.min(100, val));
    setUpdatingProgressId(orderId);
    try {
      const { error } = await supabase
        .from("orders")
        .update({ completion_percentage: val })
        .eq("id", orderId);

      if (error) {
        console.error("[AdminOrders] update progress error:", error);
        showToast(`完成度更新失敗：${error.message}`, "error");
      } else {
        showToast(`完成度已更新為 ${val}%`, "success");
        // Refresh table locally
        setOrders((prev) =>
          prev.map((o) =>
            o.id === orderId ? { ...o, completion_percentage: val } : o
          )
        );
        // Notify frontend to refresh via Realtime fallback
        window.dispatchEvent(new CustomEvent("orders:refresh"));
      }
    } catch (err) {
      console.error("[AdminOrders] update progress exception:", err);
      showToast("完成度更新時發生異常，請稍後重試", "error");
    } finally {
      setUpdatingProgressId(null);
      setEditingProgressId(null);
    }
  };

  const handleProgressKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    orderId: string
  ) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    }
    if (e.key === "Escape") {
      setEditingProgressId(null);
      setEditingProgressValue("");
    }
  };

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const filtered = useMemo(() => {
    let result = [...orders];
    if (activeFilter !== "all") {
      result = result.filter((o) => {
        const effectiveType = o.type || o.products?.type || "";
        return effectiveType === activeFilter;
      });
    }
    if (debouncedSearchTerm.trim()) {
      const term = debouncedSearchTerm.trim().toLowerCase();
      result = result.filter((o) => {
        const user = profiles?.[o.user_id];
        const userName = (user?.name || "").toLowerCase();
        const productName = (o.product_name || "").toLowerCase();
        return userName.includes(term) || productName.includes(term);
      });
    }
    return result;
  }, [activeFilter, debouncedSearchTerm, orders, profiles]);

  return (
    <div className="flex flex-col gap-4">
      {/* Toast notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-[70] px-4 py-3 rounded-lg shadow-lg font-body text-sm font-medium transition-all ${
          toast.type === "success"
            ? "bg-emerald-600 text-white"
            : "bg-red-500 text-white"
        }`}>
          <div className="flex items-center gap-2">
            <i className={toast.type === "success" ? "ri-check-line" : "ri-error-warning-line"} />
            {toast.message}
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={showConfirm}
        title="確認標記訂單完成？"
        message={
          confirmOrder
            ? `訂單「${confirmOrder.product_name}」將標記為已完成，並自動返還 NT$ ${computeRefundAmount(confirmOrder).toLocaleString()}（含認購成本、商品利潤與額外分潤）至會員錢包。若錢包更新失敗，訂單狀態不會變更。`
            : ""
        }
        confirmLabel="確認完成並返款"
        cancelLabel="取消"
        onConfirm={handleConfirmComplete}
        onCancel={() => {
          setShowConfirm(false);
          setConfirmOrder(null);
        }}
      />

      {/* Filter + Search */}
      {fetchError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-error-warning-line text-red-500 text-sm" />
          </div>
          <p className="font-body text-xs text-red-600">{fetchError}</p>
        </div>
      )}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2 bg-white rounded-lg border border-navy-100 p-1">
          {[
            { key: "all", label: "全部" },
            { key: "trial", label: "體驗區" },
            { key: "vip", label: "VIP 區" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveFilter(tab.key as typeof activeFilter)}
              className={`font-body text-xs font-medium px-3 py-1.5 rounded-md whitespace-nowrap cursor-pointer ${
                activeFilter === tab.key
                  ? "bg-navy-700 text-white"
                  : "text-navy-500 hover:text-navy-700 hover:bg-navy-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 bg-white rounded-lg border border-navy-100 px-3 py-2 w-full sm:w-64">
          <div className="w-5 h-5 flex items-center justify-center text-navy-400">
            <i className="ri-search-line text-base" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="搜尋會員或貨源名稱..."
            className="flex-1 font-body text-sm text-navy-900 placeholder-navy-400 outline-none bg-transparent"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="w-6 h-6 flex items-center justify-center rounded-md hover:bg-navy-50 text-navy-400 hover:text-navy-700 cursor-pointer"
            >
              <i className="ri-close-line text-sm" />
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-navy-100 bg-navy-50/50">
                {["訂單編號", "會員", "貨源名稱", "認購金額", "區域", "狀態", "日期", "操作"].map((h) => (
                  <th key={h} className="font-body text-[11px] font-semibold text-navy-600 text-left px-4 py-3 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-navy-100">
              {loading && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <p className="font-body text-sm text-navy-400">載入中...</p>
                  </td>
                </tr>
              )}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <p className="font-body text-sm text-navy-400">
                      {searchTerm ? "沒有符合搜尋條件的訂單" : "暫無訂單資料"}
                    </p>
                  </td>
                </tr>
              )}

              {!loading && filtered.map((o) => {
                const user = profiles?.[o.user_id] || { name: "未知用戶", phone: "—" };
                const pct = o.completion_percentage || 0;
                const isCompleting = completingId === o.id;
                return (
                  <tr key={o.id}>
                    <td className="px-4 py-3 font-body text-xs text-navy-500 font-mono">{o.id.slice(0, 8)}...</td>
                    <td className="px-4 py-3">
                      <p className="font-body text-sm text-navy-900">{user.name}</p>
                      <p className="font-body text-[10px] text-navy-400">{user.phone}</p>
                    </td>
                    <td className="px-4 py-3 font-body text-sm text-navy-900">{o.product_name}</td>
                    <td className="px-4 py-3 font-body text-sm font-semibold text-navy-900">
                      NT$ {o.amount.toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-body text-[10px] font-semibold px-2 py-1 rounded-full border ${
                        (o.type || o.products?.type) === "vip"
                          ? "bg-amber-50 text-amber-600 border-amber-200"
                          : "bg-navy-50 text-navy-500 border-navy-200"
                      }`}>
                        {(o.type || o.products?.type) === "vip" ? "VIP" : "體驗"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        <span className={`font-body text-[10px] font-semibold px-2 py-1 rounded-full border w-fit ${
                          o.status === "active"
                            ? "bg-emerald-50 text-emerald-600 border-emerald-200"
                            : "bg-navy-50 text-navy-500 border-navy-200"
                        }`}>
                          {o.status === "active" ? "進行中" : "已完成"}
                        </span>
                        {o.status === "active" && (
                          <div className="flex items-center gap-1">
                            {editingProgressId === o.id ? (
                              <input
                                type="number"
                                min={0}
                                max={100}
                                autoFocus
                                disabled={updatingProgressId === o.id}
                                value={editingProgressValue}
                                onChange={(e) => {
                                  const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 3);
                                  setEditingProgressValue(val);
                                }}
                                onBlur={() => handleProgressBlur(o.id)}
                                onKeyDown={(e) => handleProgressKeyDown(e, o.id)}
                                className="w-14 font-body text-[10px] px-1.5 py-0.5 rounded border border-navy-200 outline-none focus:border-emerald-400 text-navy-700 bg-white disabled:opacity-50"
                              />
                            ) : (
                              <button
                                onClick={() => {
                                  setEditingProgressId(o.id);
                                  setEditingProgressValue(String(pct));
                                }}
                                disabled={updatingProgressId === o.id}
                                className="font-body text-[10px] text-navy-400 hover:text-emerald-600 flex items-center gap-0.5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {updatingProgressId === o.id ? (
                                  <>
                                    <i className="ri-loader-4-line animate-spin" />
                                    更新中
                                  </>
                                ) : (
                                  <>
                                    完成度 {pct}%
                                    <i className="ri-edit-line text-[10px]" />
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-body text-xs text-navy-500">{o.created_at?.slice(0, 10) || "—"}</td>
                    <td className="px-4 py-3">
                      {o.status === "active" && (
                        <button
                          onClick={() => handleCompleteClick(o)}
                          disabled={isCompleting}
                          className={`font-body text-[10px] font-semibold px-2.5 py-1.5 rounded-md border whitespace-nowrap cursor-pointer transition-colors ${
                            isCompleting
                              ? "bg-navy-100 text-navy-400 border-navy-200 cursor-not-allowed"
                              : "bg-navy-700 text-white border-navy-700 hover:bg-navy-800"
                          }`}
                        >
                          {isCompleting ? (
                            <span className="flex items-center gap-1">
                              <i className="ri-loader-4-line animate-spin" />
                              處理中
                            </span>
                          ) : (
                            "標記完成"
                          )}
                        </button>
                      )}
                      {o.status === "completed" && (
                        <span className="font-body text-[10px] text-navy-400">已結算</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}